'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  area: string;
  project: string;
}

interface FormStatus {
  type: 'idle' | 'sending' | 'success' | 'error';
  message: string;
}

export default function ContactSection() {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    area: '',
    project: ''
  });
  const [status, setStatus] = useState<FormStatus>({ type: 'idle', message: '' });

  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.firstName.trim() || !form.email.trim()) {
        setStatus({ type: 'error', message: 'Please fill in your first name and email.' });
        return;
      }
      setStatus({ type: 'sending', message: '' });

      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });

        const data = await res.json();

        if (!res.ok) {
          setStatus({ type: 'error', message: data.error || 'Something went wrong. Please try again.' });
          return;
        }

        setStatus({
          type: 'success',
          message: "Thank you! John will be in touch shortly to arrange your free survey."
        });
        setForm({ firstName: '', lastName: '', email: '', phone: '', area: '', project: '' });
      } catch {
        setStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
      }
    },
    [form]
  );

  const inputClass =
  'w-full bg-transparent border-b-2 py-3 text-[#0F1C2E] text-base placeholder-[#7B8A94]/60 outline-none transition-all duration-200 border-[#E8DED1] focus:border-[#0F1C2E]';
  const labelClass =
  'text-[#0F1C2E] text-xs font-semibold tracking-widest uppercase mb-2 block';

  return (
    <section
      id="contact"
      className="py-32 bg-[#E8DED1]/40"
      aria-labelledby="contact-heading">
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16 reveal-hidden">
          <p className="text-[#7B8A94] text-sm font-medium tracking-[0.25em] uppercase mb-4">
            Get in Touch
          </p>
          <h2
            id="contact-heading"
            className="font-display text-[#0F1C2E] text-4xl lg:text-5xl font-medium">
            
            Request a Free Survey
          </h2>
          <p className="text-[#7B8A94] text-lg mt-4 max-w-xl mx-auto leading-relaxed">
            Tell us about your project and John will be in touch to arrange a
            no-obligation home survey at your convenience.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left — image + contact */}
          <div ref={leftRef} className="space-y-8 reveal-hidden-left">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_15b78ea6d-1782036404086.png"
                alt="Plantation shutters in a Norfolk drawing room, warm light through louvred panels"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
            </div>

            <div className="space-y-4">
              <a
                href="tel:07935750565"
                className="flex items-center gap-4 group"
                aria-label="Call John on 07935 750 565">
                
                <div className="w-12 h-12 rounded-full bg-[#0F1C2E] flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[#7B8A94] text-xs uppercase tracking-widest mb-0.5">
                    Telephone
                  </p>
                  <p className="text-[#0F1C2E] font-semibold text-lg group-hover:text-[#7B8A94] transition-colors">
                    07935 750 565
                  </p>
                </div>
              </a>

              <a
                href="mailto:John@johnkeithshutters.co.uk"
                className="flex items-center gap-4 group"
                aria-label="Email John@johnkeithshutters.co.uk">
                
                <div className="w-12 h-12 rounded-full bg-[#0F1C2E] flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-[#7B8A94] text-xs uppercase tracking-widest mb-0.5">
                    Email
                  </p>
                  <p className="text-[#0F1C2E] font-semibold text-base group-hover:text-[#7B8A94] transition-colors break-all">
                    John@johnkeithshutters.co.uk
                  </p>
                </div>
              </a>

              <a
                href="https://www.facebook.com/johnkeithshutters"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                aria-label="John Keith Shutters on Facebook">
                
                <div className="w-12 h-12 rounded-full bg-[#0F1C2E] flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[#7B8A94] text-xs uppercase tracking-widest mb-0.5">
                    Facebook
                  </p>
                  <p className="text-[#0F1C2E] font-semibold text-base group-hover:text-[#7B8A94] transition-colors">
                    John Keith Shutters
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div
            ref={rightRef}
            className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-[#E8DED1] reveal-hidden-right">
            
            {status.type === 'success' ?
            <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#0F1C2E] flex items-center justify-center mx-auto mb-5">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="font-display text-[#0F1C2E] text-2xl font-medium mb-3">
                  Enquiry Sent!
                </h3>
                <p className="text-[#7B8A94]">{status.message}</p>
                <button
                onClick={() => setStatus({ type: 'idle', message: '' })}
                className="mt-6 text-sm text-[#0F1C2E] hover:text-[#7B8A94] transition-colors underline">
                
                  Send another message
                </button>
              </div> :

            <form onSubmit={handleSubmit} noValidate aria-label="Quote request form">
                <h3 className="font-display text-[#0F1C2E] text-2xl font-medium mb-1">
                  Quote Request Form
                </h3>
                <p className="text-[#7B8A94] text-sm mb-8">
                  Please enter your contact details
                </p>

                <div className="space-y-8">
                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className={labelClass}>
                        First Name *
                      </label>
                      <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      placeholder="John"
                      value={form.firstName}
                      onChange={handleChange}
                      className={inputClass} />
                    
                    </div>
                    <div>
                      <label htmlFor="lastName" className={labelClass}>
                        Last Name
                      </label>
                      <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Smith"
                      value={form.lastName}
                      onChange={handleChange}
                      className={inputClass} />
                    
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      Email *
                    </label>
                    <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass} />
                  
                  </div>

                  {/* Phone + Area */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className={labelClass}>
                        Phone
                      </label>
                      <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="07700 900000"
                      value={form.phone}
                      onChange={handleChange}
                      className={inputClass} />
                    
                    </div>
                    <div>
                      <label htmlFor="area" className={labelClass}>
                        Your Area
                      </label>
                      <select
                      id="area"
                      name="area"
                      value={form.area}
                      onChange={handleChange}
                      className={`${inputClass} cursor-pointer`}>
                      
                        <option value="">Select area</option>
                        <option value="Burnham Market">Burnham Market</option>
                        <option value="Brancaster">Brancaster</option>
                        <option value="Holt">Holt</option>
                        <option value="Hunstanton">Hunstanton</option>
                        <option value="Other North Norfolk">Other North Norfolk</option>
                      </select>
                    </div>
                  </div>

                  {/* Project */}
                  <div>
                    <label htmlFor="project" className={labelClass}>
                      Your Project
                    </label>
                    <textarea
                    id="project"
                    name="project"
                    rows={4}
                    placeholder="Tell us about the rooms, number of windows, and anything else you'd like us to know…"
                    value={form.project}
                    onChange={handleChange}
                    className={`${inputClass} resize-none`} />
                  
                  </div>

                  {/* Error */}
                  {status.type === 'error' &&
                <p className="text-sm text-red-600 flex items-center gap-2" role="alert">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4M12 16h.01" />
                      </svg>
                      {status.message}
                    </p>
                }

                  {/* Submit */}
                  <button
                  type="submit"
                  disabled={status.type === 'sending'}
                  className="w-full bg-[#0F1C2E] text-white font-medium py-4 rounded-full text-base hover:bg-[#1B3A5C] transition-colors disabled:opacity-60 min-h-[48px] flex items-center justify-center gap-2">
                  
                    {status.type === 'sending' ?
                  <>
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                        Sending…
                      </> :

                  'Request Free Survey'
                  }
                  </button>
                </div>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

}